import { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import QRCode from 'react-qr-code'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import MarkUnreadChatAltOutlinedIcon from '@mui/icons-material/MarkUnreadChatAltOutlined'
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  useTheme
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  CareerMentorTemplateName,
  careerMentorService
} from '@/data/services/career-mentor/career-mentor-service'
import { i18nTranslationHelper } from '@/i18n'
import { AnalyticsEventName, AnalyticsTracker } from '@/infra/tracking'
import PhoneField from '../input/phone-field/phone-field'

const CALLBACK_COUNTDOWN_SECONDS = 600
const CALLBACK_TIMESTAMP_KEY = 'career-mentor:callback-requested-at'
const WHATSAPP_PHONE_NUMBER = '5511936209800'
const WHATSAPP_BASE_URL = `https://wa.me/${WHATSAPP_PHONE_NUMBER}`
const QR_CODE_SIZE = 140

/** ==================== Types ==================== */
type PhoneFlowStep = 'collect-phone' | 'confirm-phone'
type FlowStep = PhoneFlowStep | 'choose-contact'

type PhoneFormValues = {
  phone: string
}

export type CareerMentorFlowModalProps = {
  open: boolean
  onClose: () => void
  templateName: CareerMentorTemplateName
  searchTerm?: string
  location: string
}

type ModalHeaderProps = {
  titleKey: string
  descriptionKey: string
}

type PhoneStepProps = {
  step: PhoneFlowStep
  defaultPhone?: string
  onSubmit: (phone: string) => Promise<void>
  isLoading: boolean
}

type ChooseContactStepProps = {
  callbackCountdownSeconds: number
  onRequestCallback: () => Promise<void>
  onClose: () => void
  isLoading: boolean
  location: string
}

/** ==================== Helpers ==================== */
const isPhoneStep = (step: FlowStep): step is PhoneFlowStep =>
  step === 'collect-phone' || step === 'confirm-phone'

const PHONE_STEP_I18N: Record<PhoneFlowStep, { titleKey: string; descriptionKey: string }> = {
  'collect-phone': {
    titleKey: 'careerMentorFlowModal.collectPhone.title',
    descriptionKey: 'careerMentorFlowModal.collectPhone.description'
  },
  'confirm-phone': {
    titleKey: 'careerMentorFlowModal.confirmPhone.title',
    descriptionKey: 'careerMentorFlowModal.confirmPhone.description'
  }
}

const getCallbackRemainingSeconds = (): number => {
  const raw = localStorage.getItem(CALLBACK_TIMESTAMP_KEY)
  if (!raw) return 0

  const elapsed = Math.floor((Date.now() - Number(raw)) / 1000)
  return Math.max(CALLBACK_COUNTDOWN_SECONDS - elapsed, 0)
}

const setCallbackTimestamp = () => {
  localStorage.setItem(CALLBACK_TIMESTAMP_KEY, String(Date.now()))
}

const clearCallbackTimestamp = () => {
  localStorage.removeItem(CALLBACK_TIMESTAMP_KEY)
}

const formatCountdown = (seconds: number): string => {
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0')
  const ss = String(seconds % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

/** ==================== Sub-components ==================== */

function ModalHeader({ titleKey, descriptionKey }: ModalHeaderProps) {
  const t = i18nTranslationHelper.getTComponents
  const theme = useTheme()

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
      <Box
        sx={{
          backgroundColor: theme.palette.success[50],
          borderRadius: '50%',
          width: 48,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        role="img"
        aria-label={t('whatsappModal.iconAriaLabel')}
      >
        <WhatsAppIcon sx={{ fontSize: 24, color: theme.palette.success[500] }} aria-hidden="true" />
      </Box>

      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Typography variant="h6" fontWeight="bold" textAlign="center" component="h2">
          {t(titleKey)}
        </Typography>

        <Typography variant="body1" color="text.primary" textAlign="center" whiteSpace="pre-line">
          {t(descriptionKey)}
        </Typography>
      </Box>
    </Box>
  )
}

function PhoneStep({ step, defaultPhone = '', onSubmit, isLoading }: PhoneStepProps) {
  const t = i18nTranslationHelper.getTComponents
  const { titleKey, descriptionKey } = PHONE_STEP_I18N[step]

  const methods = useForm<PhoneFormValues>({
    mode: 'onChange',
    defaultValues: { phone: defaultPhone }
  })

  const phoneValue = methods.watch('phone')
  const isInvalidPhone = !phoneValue || !!methods.formState.errors?.phone

  const handleSubmit = methods.handleSubmit(async ({ phone }) => {
    await onSubmit(phone)
  })

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={3}>
        <ModalHeader titleKey={titleKey} descriptionKey={descriptionKey} />

        <PhoneField
          name="phone"
          required
          label={t('careerMentorFlowModal.phoneLabel')}
          size="medium"
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={isLoading || isInvalidPhone}
        >
          {t('careerMentorFlowModal.advanceCta')}
        </Button>
      </Box>
    </FormProvider>
  )
}

function ChooseContactStep({
  callbackCountdownSeconds,
  onRequestCallback,
  onClose,
  isLoading,
  location
}: ChooseContactStepProps) {
  const theme = useTheme()

  useEffect(() => {
    AnalyticsTracker.trackEvent(AnalyticsEventName.CAREER_MENTOR_PAGE_VIEW, { location })
  }, [])
  const t = i18nTranslationHelper.getTComponents
  const callbackRequested = callbackCountdownSeconds > 0
  const whatsappUrl = `${WHATSAPP_BASE_URL}?text=${encodeURIComponent(
    t('careerMentorFlowModal.chooseContact.whatsappSuggestedMessage')
  )}`

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <ModalHeader
        titleKey="careerMentorFlowModal.chooseContact.title"
        descriptionKey="careerMentorFlowModal.chooseContact.description"
      />

      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            size="large"
            disabled={isLoading || callbackRequested}
            startIcon={
              callbackRequested ? (
                <HourglassTopIcon aria-hidden="true" />
              ) : (
                <Badge
                  variant="dot"
                  sx={{
                    '& .MuiBadge-dot': {
                      backgroundColor: theme.palette.success[500],
                      top: 3,
                      right: 5
                    }
                  }}
                >
                  <MarkUnreadChatAltOutlinedIcon aria-hidden="true" />
                </Badge>
              )
            }
            onClick={onRequestCallback}
            sx={{
              ...(callbackRequested && {
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                  borderColor: 'transparent',
                  color: theme.palette.grey[300]
                }
              })
            }}
          >
            {callbackRequested
              ? t('careerMentorFlowModal.chooseContact.callbackSent', {
                  countdown: formatCountdown(callbackCountdownSeconds)
                })
              : t('careerMentorFlowModal.chooseContact.callbackCta')}
          </Button>

          {callbackRequested && (
            <Typography variant="body2" color={theme.palette.grey[900]} textAlign="center">
              {t('careerMentorFlowModal.chooseContact.callbackCountdownHint')}
            </Typography>
          )}
        </Box>

        <Button
          variant="outlined"
          color="inherit"
          fullWidth
          size="large"
          component="a"
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<WhatsAppIcon aria-hidden="true" />}
          onClick={() =>
            AnalyticsTracker.trackEvent(AnalyticsEventName.CAREER_MENTOR_WHATSAPP_LINK_CLICKED, {
              location
            })
          }
        >
          {t('careerMentorFlowModal.chooseContact.whatsappCta')}
        </Button>
      </Box>

      <Box display={{ xs: 'none', sm: 'flex' }} flexDirection="row" alignItems="center" gap={3}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1} flex={1}>
          <Typography variant="body1" fontWeight={700} textAlign="center" color="text.primary">
            {t('careerMentorFlowModal.chooseContact.qrCodeLabel')}
          </Typography>

          <Typography variant="body2" textAlign="center" color="text.primary">
            {t('careerMentorFlowModal.chooseContact.qrCodeDescription')}
          </Typography>
        </Box>

        <Box
          sx={{
            p: 1.5,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 4,
            display: 'flex',
            justifyContent: 'center',
            flexShrink: 0
          }}
          role="img"
          aria-label={t('careerMentorFlowModal.chooseContact.qrCodeAriaLabel')}
        >
          <QRCode value={whatsappUrl} size={QR_CODE_SIZE} level="M" />
        </Box>
      </Box>

      <Button variant="contained" fullWidth onClick={onClose} size="large">
        {t('careerMentorFlowModal.understoodCta')}
      </Button>
    </Box>
  )
}

/** ==================== Main Component ==================== */
export default function CareerMentorFlowModal({
  open,
  onClose,
  templateName,
  searchTerm,
  location
}: CareerMentorFlowModalProps) {
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const [stepOverride, setStepOverride] = useState<FlowStep | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [callbackCountdownSeconds, setCallbackCountdownSeconds] = useState(() =>
    getCallbackRemainingSeconds()
  )
  const { data: profileData, isLoading: isProfileLoading } = useQuery({
    queryKey: ['career-mentor', 'profile'],
    queryFn: careerMentorService.fetchProfile,
    enabled: open,
    staleTime: 0
  })

  const derivedStep: FlowStep = !profileData?.phone
    ? 'collect-phone'
    : !profileData?.verifiedPhone
      ? 'confirm-phone'
      : 'choose-contact'

  const step = isProfileLoading ? 'collect-phone' : stepOverride ?? derivedStep

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement
    } else {
      setStepOverride(null)
      previousFocusRef.current?.focus()
    }
  }, [open])

  useEffect(() => {
    if (callbackCountdownSeconds <= 0) {
      clearCallbackTimestamp()
      return
    }

    const id = setTimeout(() => setCallbackCountdownSeconds(prev => prev - 1), 1000)
    return () => clearTimeout(id)
  }, [callbackCountdownSeconds])

  const handlePhoneSubmit = async (phone: string) => {
    if (phone === profileData?.phone) {
      setStepOverride('choose-contact')
      return
    }

    setIsLoading(true)
    try {
      await careerMentorService.updatePhone({
        phone,
        personalEmail: profileData?.personalEmail,
        birthDate: profileData?.birthDate
      })

      setStepOverride('choose-contact')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRequestCallback = async () => {
    AnalyticsTracker.trackEvent(AnalyticsEventName.CAREER_MENTOR_REQUEST_MESSAGE_CLICKED, {
      location
    })
    setIsLoading(true)

    try {
      await careerMentorService.sendActiveMessage({
        templateName,
        language: i18nTranslationHelper.getCurrentLanguage(),
        searchTerm
      })

      setCallbackTimestamp()
      setCallbackCountdownSeconds(CALLBACK_COUNTDOWN_SECONDS)
    } finally {
      setIsLoading(false)
    }
  }

  const renderContent = () => {
    if (isProfileLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress />
        </Box>
      )
    }

    if (isPhoneStep(step)) {
      return (
        <PhoneStep
          key={step}
          step={step}
          defaultPhone={step === 'confirm-phone' ? profileData?.phone ?? '' : ''}
          onSubmit={handlePhoneSubmit}
          isLoading={isLoading}
        />
      )
    }

    return (
      <ChooseContactStep
        callbackCountdownSeconds={callbackCountdownSeconds}
        onRequestCallback={handleRequestCallback}
        onClose={onClose}
        isLoading={isLoading}
        location={location}
      />
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="career-mentor-modal-title"
      aria-describedby="career-mentor-modal-description"
    >
      <DialogContent>{renderContent()}</DialogContent>
    </Dialog>
  )
}
